// Helper zur Ableitung eines Google-Places-Bias aus dem konfigurierten
// Betriebsgebiet (siehe docs/betriebsgebiet-vorbestellungs-disposition-konzept.md,
// Abschnitt "Google Places Integration").
//
// Ziel: lokale Adressvorschläge bevorzugen, ohne externe Adressen hart
// auszuschließen. Es wird daher ein *Bias* (weiche Priorisierung) erzeugt,
// keine *Restriction*.
//
// Quelle des Betriebsgebiets ist `state.settings.global` (Firestore
// appSettings/general):
//   - serviceAreaEnabled: boolean
//   - serviceAreaMode: 'polygon' | 'radius' | 'disabled'
//   - serviceAreaPolygons[].points: [{ lat, lng }]
//   - serviceAreaPolygons[].bounds: { north, south, east, west }
//   - serviceAreaFallbackLat / serviceAreaFallbackLng: number (Radius-Fallback)

// Fester Radius-Fallback laut Konzept (Impressumsadresse + 10 km).
export const SERVICE_AREA_FALLBACK_RADIUS_METERS = 10000;

const EARTH_METERS_PER_DEGREE_LAT = 111320;

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

const calculateBoundsFromPoints = (points) => {
  if (!Array.isArray(points) || points.length === 0) return null;
  const valid = points.filter(
    (p) => p && isFiniteNumber(p.lat) && isFiniteNumber(p.lng)
  );
  if (valid.length === 0) return null;

  return valid.reduce(
    (bounds, point) => ({
      north: Math.max(bounds.north, point.lat),
      south: Math.min(bounds.south, point.lat),
      east: Math.max(bounds.east, point.lng),
      west: Math.min(bounds.west, point.lng),
    }),
    {
      north: valid[0].lat,
      south: valid[0].lat,
      east: valid[0].lng,
      west: valid[0].lng,
    }
  );
};

const isValidBounds = (bounds) =>
  bounds &&
  isFiniteNumber(bounds.north) &&
  isFiniteNumber(bounds.south) &&
  isFiniteNumber(bounds.east) &&
  isFiniteNumber(bounds.west);

// Bounding-Box um einen Mittelpunkt mit gegebenem Radius (Meter).
const boundsFromCircle = (lat, lng, radiusMeters) => {
  const deltaLat = radiusMeters / EARTH_METERS_PER_DEGREE_LAT;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const deltaLng =
    radiusMeters / (EARTH_METERS_PER_DEGREE_LAT * (Math.abs(cosLat) < 1e-6 ? 1e-6 : cosLat));

  return {
    north: lat + deltaLat,
    south: lat - deltaLat,
    east: lng + deltaLng,
    west: lng - deltaLng,
  };
};

const getActivePolygonPoints = (polygons) => {
  if (!Array.isArray(polygons) || polygons.length === 0) return null;
  const active =
    polygons.find((polygon) => polygon?.active !== false) || polygons[0];
  const points = active?.points || active?.polygon || [];
  return { active, points: Array.isArray(points) ? points : [] };
};

/**
 * Leitet aus den globalen Einstellungen einen Places-Bias ab.
 *
 * @param {object} globalSettings - state.settings.global
 * @returns {{ bounds: {north,south,east,west}, locationBias: object } | null}
 *   `bounds` ist ein LatLngBoundsLiteral (für die alte Autocomplete-API),
 *   `locationBias` ist direkt als Option der neuen PlaceAutocompleteElement-API
 *   verwendbar (CircleLiteral im Radius-Modus, sonst BoundsLiteral).
 *   `null`, wenn kein nutzbares Betriebsgebiet konfiguriert ist.
 */
export const getServiceAreaBias = (globalSettings) => {
  if (!globalSettings || globalSettings.serviceAreaEnabled !== true) {
    return null;
  }

  const mode = globalSettings.serviceAreaMode;

  // 1. Polygon-Modus: gespeicherte Bounds nutzen, sonst aus Punkten berechnen.
  if (mode === 'polygon') {
    const polygonData = getActivePolygonPoints(globalSettings.serviceAreaPolygons);
    if (polygonData) {
      const storedBounds = isValidBounds(polygonData.active?.bounds)
        ? polygonData.active.bounds
        : null;
      const bounds = storedBounds || calculateBoundsFromPoints(polygonData.points);
      if (isValidBounds(bounds)) {
        return { bounds, locationBias: bounds };
      }
    }
    // Kein gültiges Polygon -> ggf. Radius-Fallback unten versuchen.
  }

  // 2. Radius-Fallback: Mittelpunkt (Impressumsadresse) + fester Radius.
  const lat = globalSettings.serviceAreaFallbackLat;
  const lng = globalSettings.serviceAreaFallbackLng;
  if (isFiniteNumber(lat) && isFiniteNumber(lng)) {
    const radius = SERVICE_AREA_FALLBACK_RADIUS_METERS;
    return {
      bounds: boundsFromCircle(lat, lng, radius),
      locationBias: { center: { lat, lng }, radius },
    };
  }

  return null;
};

export default getServiceAreaBias;
